import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X, Loader2, MessageSquare, ClipboardList, Sparkles, ListChecks, HandHeart,
  Download, AlertTriangle, Heart, Edit3, MapPin, Calendar, Pentagon, Eye,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  conversation: MessageSquare,
  survey_collected: ClipboardList,
  interested_in_updates: Sparkles,
  follow_up_requested: ListChecks,
  supplies_requested: HandHeart,
  flyer_dropped: Download,
  no_answer: AlertTriangle,
  other: Edit3,
  donation_received: Heart,
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return ''; }
};

const Sparkline = ({ series, height = 36 }) => {
  if (!series || series.length === 0) return null;
  const w = Math.max(120, series.length * 4);
  const max = Math.max(1, ...series.map((s) => s.count));
  const stepX = w / Math.max(1, series.length - 1);
  const pts = series.map((s, i) => {
    const x = i * stepX;
    const y = height - (s.count / max) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={w} height={height} viewBox={`0 0 ${w} ${height}`} className="block">
      <polyline fill="none" stroke="#BA1826" strokeWidth="1.5" points={pts} />
      <polyline fill="rgba(186,24,38,0.08)" stroke="none"
        points={`0,${height} ${pts} ${w},${height}`} />
    </svg>
  );
};

const ZoneInsightsDrilldown = ({ open, zoneId, zoneName, onClose }) => {
  const [data, setData] = useState(null);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (!open || !zoneId) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const [a, b] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/outreach/insights/zone/${zoneId}`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/admin/outreach/insights/timeseries?days=${days}&zone_id=${zoneId}`, { withCredentials: true }),
        ]);
        if (!cancelled) { setData(a.data); setSeries(b.data?.series || []); }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load drilldown');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, zoneId, days]);

  const exportZone = () => {
    if (!zoneId) return;
    window.location.href = `${BACKEND_URL}/api/admin/outreach/logs/export.csv?zone_id=${zoneId}`;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex" data-testid="zone-drilldown">
      <div className="flex-1" onClick={onClose} />
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl flex flex-col">
        <div className="px-4 py-3 border-b border-[#E5E7EB] sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Zone drilldown</p>
              <h3 className="font-heading text-base font-bold text-[#0A0A0A]">{zoneName || data?.zone?.name || 'Zone'}</h3>
            </div>
            <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid="zone-drilldown-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-1 mt-2">
            {[7, 30, 90, 365].map((d) => (
              <button key={d} onClick={() => setDays(d)}
                className={`text-[11px] px-2 py-0.5 rounded border ${
                  days === d ? 'bg-[#BA1826] text-white border-[#BA1826]' : 'bg-white border-[#E5E7EB] text-[#4B5563]'
                }`}
                data-testid={`drilldown-range-${d}`}>
                {d === 365 ? '1y' : `${d}d`}
              </button>
            ))}
            <div className="flex-1" />
            <button onClick={exportZone}
              className="text-[11px] px-2 py-0.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1"
              data-testid="drilldown-export">
              <Download className="w-3 h-3" /> CSV
            </button>
          </div>
        </div>

        <div className="flex-1 p-4 space-y-4">
          {loading && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" /></div>}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-xs">{error}</div>}

          {data && (
            <>
              {/* Hero */}
              <div className="bg-gradient-to-r from-[#FFF8F8] to-white border border-[#BA1826]/20 rounded p-3">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#BA1826]">Relationships in this zone</p>
                <p className="text-2xl font-heading font-bold text-[#0A0A0A]" data-testid="drilldown-total">{data.total_logs}</p>
                <div className="mt-2">
                  <Sparkline series={series} />
                  <p className="text-[10px] text-[#9CA3AF] mt-1">Last {days} days</p>
                </div>
              </div>

              {/* By type (doctrine order) */}
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-1">By interaction (donations last)</p>
                <div className="grid grid-cols-3 gap-1.5" data-testid="drilldown-bytype">
                  {(data.by_type || []).map((row) => {
                    const Ico = ICONS[row.interaction_type] || Edit3;
                    const isDon = row.interaction_type === 'donation_received';
                    return (
                      <div key={row.interaction_type}
                        className={`border rounded p-2 ${isDon ? 'border-green-300 bg-green-50' : 'border-[#E5E7EB] bg-white'}`}
                        data-testid={`drilldown-type-${row.interaction_type}`}>
                        <div className="flex items-center gap-1.5 mb-1">
                          <Ico className={`w-3 h-3 ${isDon ? 'text-green-700' : 'text-[#BA1826]'}`} />
                          <span className="text-[9px] uppercase tracking-wider font-bold text-[#4B5563] leading-tight">{row.label}</span>
                        </div>
                        <p className={`text-base font-bold ${isDon ? 'text-green-700' : 'text-[#0A0A0A]'}`}>{row.count}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Top streets */}
              {data.top_streets?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-1">Top streets</p>
                  <ul className="bg-white border border-[#E5E7EB] rounded divide-y divide-[#F1F5F9]" data-testid="drilldown-streets">
                    {data.top_streets.map((s) => (
                      <li key={s.street_id} className="px-3 py-1.5 flex justify-between text-xs">
                        <span className="text-[#0A0A0A] flex items-center gap-1.5"><MapPin className="w-3 h-3 text-[#BA1826]" /> {s.name}</span>
                        <span className="text-[#4B5563] font-semibold">{s.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Follow-ups pending */}
              {data.follow_ups_pending?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-1">
                    Follow-ups pending ({data.follow_ups_pending.length})
                  </p>
                  <ul className="bg-amber-50 border border-amber-200 rounded divide-y divide-amber-100 text-xs" data-testid="drilldown-followups">
                    {data.follow_ups_pending.slice(0, 10).map((r) => (
                      <li key={r.id} className="px-3 py-1.5">
                        <p className="text-[#0A0A0A]">
                          <Calendar className="inline w-3 h-3 mr-1" />
                          {r.follow_up_date || 'no date'} · <b>{r.neighbor_name || 'A neighbor'}</b>
                        </p>
                        {r.notes && <p className="text-[#4B5563] text-[11px] mt-0.5 truncate">{r.notes}</p>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Coverage */}
              <div className="bg-white border border-[#E5E7EB] rounded p-3" data-testid="drilldown-coverage">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-1 flex items-center gap-1">
                  <Pentagon className="w-3 h-3" /> Coverage
                </p>
                {data.coverage?.has_polygon ? (
                  <p className="text-xs text-[#4B5563]">
                    Polygon boundary set. Per-log lat/lng will be captured in Phase D (street-level pin per log) so we can show <b>% inside polygon</b> + <b>logs per km²</b> automatically.
                  </p>
                ) : (
                  <p className="text-xs text-[#9CA3AF]">
                    No boundary drawn yet. Open the Map sub-tab → Edit position → <b>Draw boundary</b> to enable coverage analytics later.
                  </p>
                )}
              </div>

              {/* Recent */}
              {data.recent_logs?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-1">Recent activity</p>
                  <ul className="bg-white border border-[#E5E7EB] rounded divide-y divide-[#F1F5F9] text-xs max-h-60 overflow-y-auto" data-testid="drilldown-recent">
                    {data.recent_logs.map((r) => (
                      <li key={r.id} className="px-3 py-1.5">
                        <span className="text-[#9CA3AF] text-[10px]">{fmtDate(r.created_at)}</span>
                        <span className="text-[#0A0A0A] ml-1"><Eye className="inline w-3 h-3 mr-1" />{r.neighbor_name || 'A neighbor'}</span>
                        <span className="text-[#4B5563]"> · {r.interaction_type.replace(/_/g, ' ')}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ZoneInsightsDrilldown;
